/**
 * Página da família — spec 0067 (C-03, FR-014 a FR-016, RN-06) e 0071 (DD-02).
 *
 * Aberta pelo link que chega no WhatsApp, sem login. Primeiro a família prova que
 * o número é dela, com um código que chega no mesmo WhatsApp. Depois pode
 * completar o cadastro: outro responsável, quem pode buscar a criança e o ponto
 * de embarque, marcado com o GPS do celular no portão de casa. Nada vale antes de
 * a cooperativa conferir — e a página diz isso.
 *
 * Mostra só o primeiro nome da criança, a escola e o fim do telefone. Link vazado
 * não vira mapa de criança (RN-06).
 *
 * 0071: confirmado o telefone, a página passa a ser dele — todas as crianças de
 * que o número é responsável, o dia de cada uma, a falta e os pedidos na hora.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './PaginaFamilia.css';
import { type Chamar, Campo, Casca, Escolha, ErroDaPagina, criarChamada, hojeBRT, hora } from './familia-comum';
import { type CriancaDoTelefone, DiaDaFamilia } from './dia-da-familia';
import { SeusDados } from './seus-dados';

interface Dados {
  crianca: string;
  escola: string | null;
  telefoneFinal: string;
  confirmado: boolean;
  bloqueadoAte: string | null;
  podePedirCodigo: boolean;
  idadeMinimaSemTermo: number;
  criancas: CriancaDoTelefone[];
}

type Tarefa = 'ponto' | 'autorizado' | 'responsavel';
type Erros = Record<string, string>;

const PARENTESCOS = [
  { valor: 'mae', rotulo: 'Mãe' },
  { valor: 'pai', rotulo: 'Pai' },
  { valor: 'avo', rotulo: 'Avó ou avô' },
  { valor: 'tio', rotulo: 'Tia ou tio' },
  { valor: 'irmao', rotulo: 'Irmã ou irmão' },
  { valor: 'outro', rotulo: 'Outro' },
];

/** "10/03/2015" → "2015-03-10", ou null se não for uma data que existe. */
function dataIso(texto: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto);
  if (!m) return null;
  const [, d, mes, a] = m;
  const data = new Date(Number(a), Number(mes) - 1, Number(d));
  if (data.getFullYear() !== Number(a) || data.getMonth() !== Number(mes) - 1 || data.getDate() !== Number(d)) return null;
  if (data > new Date() || Number(a) < 1900) return null;
  return `${a}-${mes}-${d}`;
}

function idade(iso: string): number {
  const [a, m, d] = iso.split('-').map(Number);
  const hoje = new Date();
  let anos = hoje.getFullYear() - a;
  if (hoje.getMonth() + 1 < m || (hoje.getMonth() + 1 === m && hoje.getDate() < d)) anos -= 1;
  return anos;
}

/** Põe a barra sozinho: "10032015" → "10/03/2015". */
function mascaraData(texto: string): string {
  const n = texto.replace(/\D/g, '').slice(0, 8);
  return [n.slice(0, 2), n.slice(2, 4), n.slice(4)].filter(Boolean).join('/');
}

/** Leva o foco ao primeiro campo com erro, na ordem em que aparecem. */
function focarPrimeiro(erros: Erros, ordem: string[]): void {
  const id = ordem.find((c) => erros[c]);
  if (id) document.getElementById(id)?.focus();
}

const PaginaFamilia: React.FC<{ token: string }> = ({ token }) => {
  const chamar = useMemo(() => criarChamada('/api/familia', { 'x-familia-token': token }), [token]);

  const [dados, setDados] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [linkInvalido, setLinkInvalido] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [codigoEnviadoAte, setCodigoEnviadoAte] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [erroCodigo, setErroCodigo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [acabouDeConfirmar, setAcabouDeConfirmar] = useState(false);

  const [tarefa, setTarefa] = useState<Tarefa | null>(null);
  const [enviados, setEnviados] = useState<Record<Tarefa, string[]>>({ ponto: [], autorizado: [], responsavel: [] });
  const ultimaTarefa = useRef<Tarefa | null>(null);

  useEffect(() => { document.title = 'Transporte escolar · Coopertransmig'; }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setDados(await chamar<Dados>(''));
    } catch (e) {
      if (e instanceof ErroDaPagina && e.codigo === 'LINK_INVALIDO') setLinkInvalido(true);
      else setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [chamar]);

  useEffect(() => { carregar(); }, [carregar]);

  // Ao abrir um formulário o foco vai para o título dele; ao fechar, volta ao cartão.
  useEffect(() => {
    if (tarefa) document.getElementById('fam-form-titulo')?.focus();
    else if (ultimaTarefa.current) document.getElementById(`fam-tarefa-${ultimaTarefa.current}`)?.focus();
  }, [tarefa]);

  const abrir = (t: Tarefa) => { ultimaTarefa.current = t; setTarefa(t); };
  const concluir = (t: Tarefa, texto: string) => {
    setEnviados((e) => ({ ...e, [t]: [...e[t], texto] }));
    setTarefa(null);
  };

  const pedirCodigo = async () => {
    setErro(null);
    setErroCodigo(null);
    setOcupado(true);
    try {
      const r = await chamar<{ validoAte: string }>('/codigo', { method: 'POST' });
      setCodigoEnviadoAte(r.validoAte);
      setCodigo('');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  const confirmar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (codigo.length !== 6) {
      setErroCodigo('O código tem 6 números.');
      document.getElementById('fam-codigo')?.focus();
      return;
    }
    setErroCodigo(null);
    setOcupado(true);
    try {
      await chamar('/confirmar', { method: 'POST', body: JSON.stringify({ codigo }) });
      setCodigoEnviadoAte(null);
      setAcabouDeConfirmar(true);
      await carregar();
    } catch (e2) {
      if (e2 instanceof ErroDaPagina && e2.codigo === 'BLOQUEADO') {
        setCodigoEnviadoAte(null);
        await carregar();
      } else if (e2 instanceof ErroDaPagina && e2.codigo !== 'REDE') {
        setErroCodigo(e2.message);
        document.getElementById('fam-codigo')?.focus();
      } else {
        setErro((e2 as Error).message);
      }
    } finally {
      setOcupado(false);
    }
  };

  if (carregando && !dados) {
    return <Casca><p className="fam-apoio" role="status">Carregando…</p></Casca>;
  }

  if (linkInvalido) {
    return (
      <Casca>
        <h1 className="fam-titulo">Este link não vale mais</h1>
        <p className="fam-apoio">
          Ele pode ter expirado ou sido trocado por um mais novo. Se o seu telefone já foi confirmado, entre com ele: um
          código chega no seu WhatsApp. Se ainda não foi, peça um novo link à escola ou à cooperativa.
        </p>
        <a className="fam-btn fam-btn-primario" href="/familia/entrar">Entrar com o meu telefone</a>
      </Casca>
    );
  }

  if (!dados) {
    return (
      <Casca>
        <h1 className="fam-titulo">Não foi possível abrir</h1>
        <p className="fam-apoio">{erro}</p>
        <button type="button" className="fam-btn fam-btn-primario" onClick={carregar}>Tentar de novo</button>
      </Casca>
    );
  }

  const bloqueado = dados.bloqueadoAte != null;
  const criancas = dados.criancas;
  const nomes = criancas.length > 1 ? `${criancas.slice(0, -1).map((c) => c.nome).join(', ')} e ${criancas[criancas.length - 1].nome}` : dados.crianca;
  const TAREFAS: { id: Tarefa; titulo: string; apoio: string }[] = [
    { id: 'ponto', titulo: 'Ponto de embarque', apoio: 'No portão de casa, o celular marca o lugar exato onde a van para. Também serve para um endereço por alguns dias.' },
    { id: 'autorizado', titulo: 'Quem mais pode buscar', apoio: 'Avó, tia, vizinha: a criança só é entregue a quem estiver na lista.' },
    { id: 'responsavel', titulo: 'Outro responsável', apoio: 'Essa pessoa também recebe os avisos, depois de confirmar o próprio telefone.' },
  ];

  return (
    <Casca>
      {!dados.confirmado ? (
        <section aria-labelledby="fam-confirmar">
          <p className="fam-contexto">{dados.crianca}{dados.escola ? ` · ${dados.escola}` : ''}</p>
          <h1 id="fam-confirmar" className="fam-titulo">Confirme que este WhatsApp é seu</h1>
          <p className="fam-apoio">
            A Coopertransmig cadastrou o número terminado em <b>{dados.telefoneFinal}</b> como responsável
            por {dados.crianca} no transporte escolar. Para confirmar, mandamos um código para este mesmo WhatsApp.
          </p>

          {bloqueado ? (
            <p className="fam-aviso" role="alert">
              Muitas tentativas erradas. Você pode pedir outro código a partir das {hora(dados.bloqueadoAte!)}.
            </p>
          ) : !codigoEnviadoAte ? (
            <button type="button" className="fam-btn fam-btn-primario" onClick={pedirCodigo} disabled={ocupado}>
              {ocupado ? 'Enviando…' : 'Receber o código no WhatsApp'}
            </button>
          ) : (
            <form onSubmit={confirmar} className="fam-form" noValidate>
              <Campo id="fam-codigo" rotulo="Código que chegou no WhatsApp" erro={erroCodigo ?? undefined}
                ajuda={`Vale até as ${hora(codigoEnviadoAte)}.`}>
                {(a11y) => (
                  <input
                    {...a11y}
                    className="fam-codigo"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                  />
                )}
              </Campo>
              <button type="submit" className="fam-btn fam-btn-primario" disabled={ocupado}>
                {ocupado ? 'Conferindo…' : 'Confirmar'}
              </button>
              <button type="button" className="fam-btn fam-btn-texto" onClick={pedirCodigo} disabled={ocupado}>
                Não chegou? Pedir outro código
              </button>
            </form>
          )}
          {erro && <p className="fam-erro" role="alert">{erro}</p>}
        </section>
      ) : (
        <section aria-labelledby="fam-ok">
          <h1 id="fam-ok" className="fam-titulo">Transporte de {nomes}</h1>
          {acabouDeConfirmar && (
            <p className="fam-ok" role="status">Telefone confirmado. Os avisos do transporte chegam neste WhatsApp.</p>
          )}

          <DiaDaFamilia chamar={chamar} criancas={criancas} />

          <section className="fam-bloco" aria-labelledby="fam-cadastro">
            <h2 id="fam-cadastro" className="fam-secao">Cadastro</h2>
            {tarefa === null ? (
              <>
                <p className="fam-apoio">Tudo o que você enviar é conferido pela cooperativa antes de valer.</p>
                <div className="fam-tarefas">
                  {TAREFAS.map((t) => (
                    <button key={t.id} id={`fam-tarefa-${t.id}`} type="button" className="fam-tarefa" onClick={() => abrir(t.id)}>
                      <span className="fam-tarefa-titulo">{t.titulo}</span>
                      <span className="fam-tarefa-apoio">{t.apoio}</span>
                      {enviados[t.id].length > 0 && (
                        <span className="fam-tarefa-enviado">
                          Enviado para conferência: {enviados[t.id].join('; ')}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            ) : tarefa === 'ponto' ? (
              <FormPonto chamar={chamar} criancas={criancas} onPronto={(t) => concluir('ponto', t)} onVoltar={() => setTarefa(null)} />
            ) : tarefa === 'autorizado' ? (
              <FormAutorizado chamar={chamar} criancas={criancas} idadeMinima={dados.idadeMinimaSemTermo}
                onPronto={(t) => concluir('autorizado', t)} onVoltar={() => setTarefa(null)} />
            ) : (
              <FormResponsavel chamar={chamar} criancas={criancas} onPronto={(t) => concluir('responsavel', t)} onVoltar={() => setTarefa(null)} />
            )}
          </section>

          <SeusDados chamar={chamar} />
        </section>
      )}
    </Casca>
  );
};


interface PropsForm {
  chamar: Chamar;
  criancas: CriancaDoTelefone[];
  onPronto: (texto: string) => void;
  onVoltar: () => void;
}

/** 0071: com duas crianças no telefone, o pedido diz de qual é. Com uma, nem aparece. */
function useCrianca(criancas: CriancaDoTelefone[]): [CriancaDoTelefone | null, React.ReactNode] {
  const [id, setId] = useState(String(criancas[0]?.idPassageiro ?? ''));
  const escolhida = criancas.find((c) => String(c.idPassageiro) === id) ?? null;
  const campo = criancas.length > 1 ? (
    <Escolha nome="fam-qual-crianca" legenda="Para qual criança" valor={id} onChange={setId}
      opcoes={criancas.map((c) => ({ valor: String(c.idPassageiro), rotulo: c.nome }))} />
  ) : null;
  return [escolhida, campo];
}

const TituloDoForm: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 id="fam-form-titulo" className="fam-subtitulo" tabIndex={-1}>{children}</h2>
);

const Rodape: React.FC<{
  ocupado: boolean; onVoltar: () => void; rotulo: string; desabilitado?: boolean;
  tipo?: 'submit' | 'button'; onEnviar?: () => void;
}> = ({ ocupado, onVoltar, rotulo, desabilitado, tipo = 'submit', onEnviar }) => (
  <div className="fam-acoes">
    <button type="button" className="fam-btn fam-btn-texto" onClick={onVoltar} disabled={ocupado}>Voltar</button>
    <button type={tipo} className="fam-btn fam-btn-primario" onClick={onEnviar} disabled={desabilitado || ocupado}>
      {ocupado ? 'Enviando…' : rotulo}
    </button>
  </div>
);

type Duracao = 'sempre' | 'dias' | 'periodo';
const DIAS_DA_SEMANA = [
  { n: 1, curto: 'Seg', longo: 'segundas' }, { n: 2, curto: 'Ter', longo: 'terças' }, { n: 3, curto: 'Qua', longo: 'quartas' },
  { n: 4, curto: 'Qui', longo: 'quintas' }, { n: 5, curto: 'Sex', longo: 'sextas' }, { n: 6, curto: 'Sáb', longo: 'sábados' },
  { n: 7, curto: 'Dom', longo: 'domingos' },
];
const juntar = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`);
type PernaPonto = 'ida' | 'volta' | 'ambas';

const FormPonto: React.FC<PropsForm> = ({ chamar, criancas, onPronto, onVoltar }) => {
  const [crianca, campoCrianca] = useCrianca(criancas);
  // 0071, FR-010: o endereço por alguns dias (casa da avó na semana de prova) tem início e fim.
  const [duracao, setDuracao] = useState<Duracao>('sempre');
  const [pernaPeriodo, setPernaPeriodo] = useState<PernaPonto>('ambas');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [errosPeriodo, setErrosPeriodo] = useState<Erros>({});
  // 0075, DD-04: o ponto de alguns dias da semana (a casa do pai nas terças), toda semana ou semana sim, semana não.
  const [dias, setDias] = useState<number[]>([]);
  const [alterna, setAlterna] = useState<'nao' | 'sim'>('nao');
  const [comeca, setComeca] = useState<'esta' | 'outra'>('esta');
  const [posicao, setPosicao] = useState<{ lat: number; lng: number; precisao: number } | null>(null);
  const [referencia, setReferencia] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [localizando, setLocalizando] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const localizar = () => {
    setErro(null);
    if (!navigator.geolocation) {
      setErro('Este celular não informa a localização. Peça ao motorista para marcar o ponto.');
      return;
    }
    setLocalizando(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPosicao({ lat: p.coords.latitude, lng: p.coords.longitude, precisao: p.coords.accuracy }); setLocalizando(false); },
      (e) => {
        setLocalizando(false);
        setErro(e.code === e.PERMISSION_DENIED
          ? 'O celular não deixou usar a localização. Libere a localização para este site e tente de novo.'
          : 'Não foi possível achar a localização agora. Tente de novo ao ar livre.');
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  const enviar = async () => {
    if (!posicao) return;
    setErro(null);
    if (duracao === 'periodo') {
      const novos: Erros = {};
      if (!de || de < hojeBRT()) novos['fam-ponto-de'] = 'Escolha o primeiro dia, de hoje em diante.';
      if (!ate || (de && ate < de)) novos['fam-ponto-ate'] = 'Escolha o último dia, no primeiro ou depois dele.';
      setErrosPeriodo(novos);
      if (Object.keys(novos).length) { focarPrimeiro(novos, ['fam-ponto-de', 'fam-ponto-ate']); return; }
    }
    if (duracao === 'dias' && dias.length === 0) {
      setErrosPeriodo({ 'fam-ponto-dias': 'Escolha pelo menos um dia.' });
      document.getElementById('fam-ponto-dia-1')?.focus();
      return;
    }
    setOcupado(true);
    try {
      const periodo = duracao === 'periodo' ? { perna: pernaPeriodo, validoDe: de, validoAte: ate }
        : duracao === 'dias' ? { perna: pernaPeriodo, diasSemana: dias, semana: alterna === 'sim' ? comeca : 'todas' }
        : { perna: 'ambas' };
      await chamar('/solicitacoes', {
        method: 'POST',
        body: JSON.stringify({
          tipo: 'ponto',
          idPassageiro: crianca?.idPassageiro,
          dados: { ...periodo, referencia: referencia.trim() || undefined },
          geo: { lat: posicao.lat, lng: posicao.lng, precisaoM: posicao.precisao },
        }),
      });
      const de_ate = duracao === 'periodo' ? `, de ${de.split('-').reverse().slice(0, 2).join('/')} a ${ate.split('-').reverse().slice(0, 2).join('/')}`
        : duracao === 'dias' ? `, nas ${juntar(DIAS_DA_SEMANA.filter((d) => dias.includes(d.n)).map((d) => d.longo))}${alterna === 'sim' ? ', semana sim, semana não' : ''}`
        : '';
      onPronto(`${crianca ? `${crianca.nome}: ` : ''}ponto com precisão de ${Math.round(posicao.precisao)} m${de_ate}`);
    } catch (e) {
      setErro((e as Error).message);
      setOcupado(false);
    }
  };

  const impreciso = posicao != null && posicao.precisao > 50;

  return (
    <div className="fam-form">
      <TituloDoForm>Ponto de embarque</TituloDoForm>
      {campoCrianca}
      <Escolha nome="fam-ponto-duracao" legenda="Vale" valor={duracao} onChange={setDuracao} desabilitado={ocupado}
        opcoes={[{ valor: 'sempre', rotulo: 'Todos os dias' }, { valor: 'dias', rotulo: 'Alguns dias da semana' }, { valor: 'periodo', rotulo: 'Por um período' }]} />
      {duracao === 'dias' && (
        <>
          <fieldset className="fam-escolha" aria-describedby={errosPeriodo['fam-ponto-dias'] ? 'fam-ponto-dias-erro' : undefined}>
            <legend className="fam-rotulo">Em quais dias</legend>
            <div className="fam-dias">
              {DIAS_DA_SEMANA.map((d) => (
                <React.Fragment key={d.n}>
                  <input type="checkbox" id={`fam-ponto-dia-${d.n}`} checked={dias.includes(d.n)} disabled={ocupado}
                    onChange={(e) => { setErrosPeriodo({}); setDias(e.target.checked ? [...dias, d.n].sort((a, b) => a - b) : dias.filter((x) => x !== d.n)); }} />
                  <label htmlFor={`fam-ponto-dia-${d.n}`}><span aria-hidden="true">{d.curto}</span><span className="fam-so-leitor">{d.longo}</span></label>
                </React.Fragment>
              ))}
            </div>
            {errosPeriodo['fam-ponto-dias'] && <p id="fam-ponto-dias-erro" className="fam-erro-campo" role="alert">{errosPeriodo['fam-ponto-dias']}</p>}
          </fieldset>
          <Escolha nome="fam-ponto-alterna" legenda="Em quais semanas" valor={alterna} onChange={setAlterna} desabilitado={ocupado}
            opcoes={[{ valor: 'nao', rotulo: 'Toda semana' }, { valor: 'sim', rotulo: 'Semana sim, semana não' }]} />
          {alterna === 'sim' && (
            <Escolha nome="fam-ponto-comeca" legenda="Começando" valor={comeca} onChange={setComeca} desabilitado={ocupado}
              opcoes={[{ valor: 'esta', rotulo: 'Nesta semana' }, { valor: 'outra', rotulo: 'Na próxima' }]} />
          )}
          <Escolha nome="fam-ponto-perna-dias" legenda="Em qual viagem" valor={pernaPeriodo} onChange={setPernaPeriodo} desabilitado={ocupado}
            opcoes={[{ valor: 'ida', rotulo: 'Ida' }, { valor: 'volta', rotulo: 'Volta' }, { valor: 'ambas', rotulo: 'As duas' }]} />
        </>
      )}
      {duracao === 'periodo' && (
        <>
          <div className="fam-datas">
            <Campo id="fam-ponto-de" rotulo="Do dia" erro={errosPeriodo['fam-ponto-de']}>
              {(a11y) => <input {...a11y} type="date" min={hojeBRT()} value={de} onChange={(e) => setDe(e.target.value)} />}
            </Campo>
            <Campo id="fam-ponto-ate" rotulo="Até o dia" erro={errosPeriodo['fam-ponto-ate']}>
              {(a11y) => <input {...a11y} type="date" min={de || hojeBRT()} value={ate} onChange={(e) => setAte(e.target.value)} />}
            </Campo>
          </div>
          <Escolha nome="fam-ponto-perna" legenda="Em qual viagem" valor={pernaPeriodo} onChange={setPernaPeriodo} desabilitado={ocupado}
            opcoes={[{ valor: 'ida', rotulo: 'Ida' }, { valor: 'volta', rotulo: 'Volta' }, { valor: 'ambas', rotulo: 'As duas' }]} />
        </>
      )}
      <p className="fam-apoio">
        {duracao === 'sempre'
          ? 'Fique no portão de casa, onde a van deve parar, e toque no botão.'
          : duracao === 'dias'
            ? 'Fique no lugar onde a van deve parar nesses dias, e toque no botão. Nos outros dias, vale o ponto de sempre.'
            : 'Fique no lugar onde a van deve parar nesses dias, e toque no botão. Depois do último dia, volta o ponto de sempre.'}
      </p>
      <button type="button" className="fam-btn fam-btn-secundario" onClick={localizar} disabled={localizando || ocupado}>
        {localizando ? 'Localizando…' : posicao ? 'Marcar de novo' : 'Usar a localização deste celular'}
      </button>
      {posicao && (
        <p className={impreciso ? 'fam-aviso' : 'fam-ok'} role="status">
          {impreciso
            ? `A localização está imprecisa (cerca de ${Math.round(posicao.precisao)} m). Vá para mais perto do portão, ao ar livre, e marque de novo.`
            : `Localização marcada, com precisão de cerca de ${Math.round(posicao.precisao)} m.`}
        </p>
      )}
      <Campo id="fam-referencia" rotulo="Como o motorista reconhece o portão (opcional)">
        {(a11y) => (
          <input {...a11y} type="text" maxLength={120} value={referencia} onChange={(e) => setReferencia(e.target.value)}
            placeholder="Ex.: portão azul, ao lado da padaria" />
        )}
      </Campo>
      {erro && <p className="fam-erro" role="alert">{erro}</p>}
      <Rodape ocupado={ocupado} onVoltar={onVoltar} rotulo="Enviar o ponto" tipo="button" onEnviar={enviar} desabilitado={!posicao} />
    </div>
  );
};

const FormAutorizado: React.FC<PropsForm & { idadeMinima: number }> = ({ chamar, criancas, idadeMinima, onPronto, onVoltar }) => {
  const [escolhida, campoCrianca] = useCrianca(criancas);
  const crianca = escolhida?.nome ?? 'a criança';
  const [nome, setNome] = useState('');
  const [parentesco, setParentesco] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [termo, setTermo] = useState<File | null>(null);
  const [erros, setErros] = useState<Erros>({});
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const iso = dataIso(nascimento);
  const precisaTermo = iso != null && idade(iso) < idadeMinima;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    const novos: Erros = {};
    if (!nome.trim()) novos['fam-aut-nome'] = 'Escreva o nome completo.';
    if (!parentesco) novos['fam-aut-parentesco'] = 'Escolha o parentesco.';
    if (!iso) novos['fam-aut-nascimento'] = 'Escreva a data como dia/mês/ano, por exemplo 10/03/1985.';
    if (precisaTermo && !termo) novos['fam-aut-termo'] = 'Anexe o termo assinado.';
    setErros(novos);
    if (Object.keys(novos).length) {
      focarPrimeiro(novos, ['fam-aut-nome', 'fam-aut-parentesco', 'fam-aut-nascimento', 'fam-aut-termo']);
      return;
    }
    setOcupado(true);
    try {
      let caminhoTermo: string | undefined;
      if (precisaTermo && termo) {
        const form = new FormData();
        form.append('arquivo', termo);
        caminhoTermo = (await chamar<{ caminhoTermo: string }>('/termo', { method: 'POST', body: form })).caminhoTermo;
      }
      await chamar('/solicitacoes', {
        method: 'POST',
        body: JSON.stringify({ tipo: 'autorizado', idPassageiro: escolhida?.idPassageiro, dados: { nome: nome.trim(), parentesco, dataNascimento: iso, caminhoTermo } }),
      });
      onPronto(criancas.length > 1 ? `${nome.trim()} (${crianca})` : nome.trim());
    } catch (e2) {
      setErro((e2 as Error).message);
      setOcupado(false);
    }
  };

  return (
    <form className="fam-form" onSubmit={enviar} noValidate>
      <TituloDoForm>Quem pode buscar {crianca}</TituloDoForm>
      {campoCrianca}
      <Campo id="fam-aut-nome" rotulo="Nome completo" erro={erros['fam-aut-nome']}>
        {(a11y) => <input {...a11y} type="text" maxLength={120} value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="off" />}
      </Campo>
      <Campo id="fam-aut-parentesco" rotulo="Parentesco" erro={erros['fam-aut-parentesco']}>
        {(a11y) => (
          <select {...a11y} value={parentesco} onChange={(e) => setParentesco(e.target.value)}>
            <option value="" disabled>Escolha</option>
            {PARENTESCOS.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
          </select>
        )}
      </Campo>
      <Campo id="fam-aut-nascimento" rotulo="Data de nascimento (dia/mês/ano)" erro={erros['fam-aut-nascimento']}>
        {(a11y) => (
          <input {...a11y} type="text" inputMode="numeric" placeholder="10/03/1985" value={nascimento}
            onChange={(e) => setNascimento(mascaraData(e.target.value))} autoComplete="off" />
        )}
      </Campo>
      {precisaTermo && (
        <Campo id="fam-aut-termo" rotulo="Termo assinado por você" erro={erros['fam-aut-termo']}
          ajuda={`Quem tem menos de ${idadeMinima} anos só pode buscar com o termo assinado pelo responsável. Foto ou PDF, até 5 MB.`}>
          {(a11y) => (
            <div className="fam-anexo">
              <input {...a11y} className="fam-anexo-entrada" type="file" accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => setTermo(e.target.files?.[0] ?? null)} />
              <span className="fam-btn fam-btn-secundario fam-anexo-botao" aria-hidden="true">
                {termo ? 'Trocar o arquivo' : 'Escolher foto ou PDF'}
              </span>
              {termo && <span className="fam-anexo-nome">{termo.name}</span>}
            </div>
          )}
        </Campo>
      )}
      {erro && <p className="fam-erro" role="alert">{erro}</p>}
      <Rodape ocupado={ocupado} onVoltar={onVoltar} rotulo="Enviar" />
    </form>
  );
};

const FormResponsavel: React.FC<PropsForm> = ({ chamar, criancas, onPronto, onVoltar }) => {
  const [escolhida, campoCrianca] = useCrianca(criancas);
  const [nome, setNome] = useState('');
  const [parentesco, setParentesco] = useState('');
  const [telefone, setTelefone] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    let digitos = telefone.replace(/\D/g, '');
    if (digitos.length === 10 || digitos.length === 11) digitos = `55${digitos}`;
    const novos: Erros = {};
    if (!nome.trim()) novos['fam-resp-nome'] = 'Escreva o nome.';
    if (!parentesco) novos['fam-resp-parentesco'] = 'Escolha o parentesco.';
    if (!/^55\d{10,11}$/.test(digitos)) novos['fam-resp-telefone'] = 'Escreva o WhatsApp com o DDD, por exemplo (31) 99999-0000.';
    setErros(novos);
    if (Object.keys(novos).length) {
      focarPrimeiro(novos, ['fam-resp-nome', 'fam-resp-parentesco', 'fam-resp-telefone']);
      return;
    }
    setOcupado(true);
    try {
      await chamar('/solicitacoes', {
        method: 'POST',
        body: JSON.stringify({ tipo: 'responsavel', idPassageiro: escolhida?.idPassageiro, dados: { nome: nome.trim(), parentesco, telefone: digitos, principal: false } }),
      });
      onPronto(nome.trim());
    } catch (e2) {
      setErro((e2 as Error).message);
      setOcupado(false);
    }
  };

  return (
    <form className="fam-form" onSubmit={enviar} noValidate>
      <TituloDoForm>Outro responsável</TituloDoForm>
      {campoCrianca}
      <Campo id="fam-resp-nome" rotulo="Nome" erro={erros['fam-resp-nome']}>
        {(a11y) => <input {...a11y} type="text" maxLength={120} value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="off" />}
      </Campo>
      <Campo id="fam-resp-parentesco" rotulo="Parentesco" erro={erros['fam-resp-parentesco']}>
        {(a11y) => (
          <select {...a11y} value={parentesco} onChange={(e) => setParentesco(e.target.value)}>
            <option value="" disabled>Escolha</option>
            {PARENTESCOS.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
          </select>
        )}
      </Campo>
      <Campo id="fam-resp-telefone" rotulo="WhatsApp com DDD" erro={erros['fam-resp-telefone']}>
        {(a11y) => (
          <input {...a11y} type="tel" inputMode="tel" autoComplete="off" value={telefone}
            onChange={(e) => setTelefone(e.target.value)} placeholder="(31) 99999-0000" />
        )}
      </Campo>
      {erro && <p className="fam-erro" role="alert">{erro}</p>}
      <Rodape ocupado={ocupado} onVoltar={onVoltar} rotulo="Enviar" />
    </form>
  );
};

export default PaginaFamilia;
